/**
 * Cornell notes page geometry and the plain-text export.
 *
 * The layout follows the one Walter Pauk described in *How to Study in College*: a cue
 * column down the left, the note-taking area to its right, and a summary strip across
 * the bottom — about 2½ inches, 6 inches and 2 inches on a letter page. Here the cue
 * column is the same 2½-inch share of the page width and the summary strip a fixed
 * 50 mm, so an A4 sheet gets the same proportions. All lengths are millimetres.
 */

export type PaperSize = "letter" | "a4";
export type Ruling = "wide" | "college" | "narrow" | "blank";

/** Sheet and margin sizes, in millimetres. */
export const PAPER: Record<
  PaperSize,
  { width: number; height: number; margin: number; css: string }
> = {
  letter: { width: 215.9, height: 279.4, margin: 12.7, css: "letter" },
  a4: { width: 210, height: 297, margin: 12, css: "A4" },
};

/**
 * Line spacing, in millimetres: US wide ruled (11/32 in), college ruled (9/32 in) and
 * narrow ruled (1/4 in). Blank draws no lines.
 */
export const RULING_MM: Record<Ruling, number> = {
  wide: 8.73,
  college: 7.14,
  narrow: 6.35,
  blank: 0,
};

/** The cue column as Pauk's share of a letter page: 2.5 of 8.5 inches. */
const CUE_SHARE = 2.5 / 8.5;
const HEADER_MM = 20;
const SUMMARY_MM = 50;
const LABEL_MM = 6;

export interface CornellGeometry {
  /** The printable area inside the margins. */
  width: number;
  height: number;
  header: number;
  cueWidth: number;
  /** Top of the cue/notes area, below the header. */
  bodyTop: number;
  bodyHeight: number;
  /** Top of the summary strip. */
  summaryTop: number;
  summaryHeight: number;
  /** y positions of the ruled lines in the cue/notes area. */
  bodyLines: number[];
  /** y positions of the ruled lines in the summary strip. */
  summaryLines: number[];
}

function linesIn(top: number, height: number, spacing: number): number[] {
  if (spacing <= 0) {
    return [];
  }
  const lines: number[] = [];
  for (let y = top + spacing; y <= top + height - spacing / 2; y += spacing) {
    lines.push(Math.round(y * 100) / 100);
  }
  return lines;
}

export function cornellGeometry(
  paper: PaperSize,
  ruling: Ruling
): CornellGeometry {
  const sheet = PAPER[paper];
  const width = Math.round((sheet.width - 2 * sheet.margin) * 100) / 100;
  const height = Math.round((sheet.height - 2 * sheet.margin) * 100) / 100;
  const bodyTop = HEADER_MM;
  const summaryTop = height - SUMMARY_MM;
  const bodyHeight = summaryTop - bodyTop;
  const spacing = RULING_MM[ruling];
  return {
    width,
    height,
    header: HEADER_MM,
    cueWidth: Math.round(sheet.width * CUE_SHARE * 10) / 10 - sheet.margin,
    bodyTop,
    bodyHeight,
    summaryTop,
    summaryHeight: SUMMARY_MM,
    bodyLines: linesIn(bodyTop + LABEL_MM, bodyHeight - LABEL_MM, spacing),
    summaryLines: linesIn(
      summaryTop + LABEL_MM,
      SUMMARY_MM - LABEL_MM,
      spacing
    ),
  };
}

export interface CornellRow {
  id: string;
  cue: string;
  note: string;
}

export interface CornellNotes {
  name: string;
  course: string;
  date: string;
  topic: string;
  rows: readonly CornellRow[];
  summary: string;
}

export interface CornellLabels {
  name: string;
  course: string;
  date: string;
  topic: string;
  cues: string;
  notes: string;
  summary: string;
}

/**
 * The notes as plain text, in reading order: header, each cue with its notes, summary.
 * Empty header fields and empty rows are left out.
 */
export function cornellText(
  notes: CornellNotes,
  labels: CornellLabels
): string {
  const header = (
    [
      [labels.topic, notes.topic],
      [labels.course, notes.course],
      [labels.date, notes.date],
      [labels.name, notes.name],
    ] as const
  )
    .filter(([, value]) => value.trim() !== "")
    .map(([label, value]) => `${label}: ${value.trim()}`);
  const rows = notes.rows
    .filter((row) => row.cue.trim() !== "" || row.note.trim() !== "")
    .map((row) => {
      const parts: string[] = [];
      if (row.cue.trim() !== "") {
        parts.push(`${labels.cues}: ${row.cue.trim()}`);
      }
      if (row.note.trim() !== "") {
        parts.push(`${labels.notes}: ${row.note.trim()}`);
      }
      return parts.join("\n");
    });
  const sections = [header.join("\n"), rows.join("\n\n")];
  if (notes.summary.trim() !== "") {
    sections.push(`${labels.summary}:\n${notes.summary.trim()}`);
  }
  return sections.filter((section) => section !== "").join("\n\n");
}

export interface CornellHeader {
  name: string;
  course: string;
  date: string;
  topic: string;
}

export interface SavedCornell {
  header: CornellHeader;
  rows: CornellRow[];
  summary: string;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Validate notes read back from storage (a JSON string, as stored).
 *
 * Anything unreadable is null; individual rows that are not the right shape are
 * dropped rather than trusted.
 */
export function parseSavedCornell(raw: string | null): SavedCornell | null {
  if (!raw) {
    return null;
  }
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== "object") {
    return null;
  }
  const record = data as Record<string, unknown>;
  const header = (record.header ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(record.rows) ? record.rows : [];
  return {
    header: {
      name: text(header.name),
      course: text(header.course),
      date: text(header.date),
      topic: text(header.topic),
    },
    rows: rows
      .filter(
        (row): row is CornellRow =>
          !!row &&
          typeof row === "object" &&
          typeof (row as CornellRow).id === "string" &&
          typeof (row as CornellRow).cue === "string" &&
          typeof (row as CornellRow).note === "string"
      )
      .map((row) => ({ id: row.id, cue: row.cue, note: row.note })),
    summary: text(record.summary),
  };
}
